import React, { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../services/logger';
import analyticsService from '../services/analytics';
import apiService, { RATE_LIMIT_MESSAGE } from '../services/api';
import { useContent } from '../hooks/useContent';
import NowPlayingBar from './NowPlayingBar';
import IdentifyPanel from './IdentifyPanel';
import StreamErrorModal from './StreamErrorModal';
import LyricsPanel from './LyricsPanel';
import AppSidebar from './AppSidebar';
import HomePage from './HomePage';
import CountriesPage from './CountriesPage';
import StationsPage from './StationsPage';
import '../styles/components/Radio.css';

const Radio = () => {
  // Content management
  useContent();

  // Theme
  const [theme, setTheme] = useState(
    () => localStorage.getItem('radio-theme') || 'dark'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('radio-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  // Essential state
  const [allStations, setAllStations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currentPlayingStation, setCurrentPlayingStation] = useState(null);
  const [currentVolume, setCurrentVolume] = useState(0.5);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playDuration, setPlayDuration] = useState(0);
  const [playStartTime, setPlayStartTime] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [lastPauseTime, setLastPauseTime] = useState(null);
  const [currentPlaySession, setCurrentPlaySession] = useState(null);

  // Navigation
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedCountry, setSelectedCountry] = useState('');

  // Song identification
  const [identifyState, setIdentifyState] = useState(null);       // null | 'loading' | {found, ...}
  const [artistLinksState, setArtistLinksState] = useState(null); // null | 'loading' | {youtube, instagram, ...}

  // Lyrics
  const [lyricsState, setLyricsState] = useState(null);    // null | 'loading' | { lyrics } | { error }
  const [lyricsTarget, setLyricsTarget] = useState(null);  // { artist, title }

  // Stream health
  const [streamError, setStreamError] = useState(null);   // null | { type, stationName, similarStations }
  const [isBuffering, setIsBuffering] = useState(false);
  const stallTimerRef = useRef(null);
  const autoRetryDoneRef = useRef(false);

  // Modal states
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // Contact form
  const [contactForm, setContactForm] = useState({
    name: '', email: '', topic: '', subject: '', message: '',
  });
  const [contactLoading, setContactLoading] = useState(false);

  // Search states
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [stationSearchTerm, setStationSearchTerm] = useState('');
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [allCountriesData, setAllCountriesData] = useState([]);
  const [countryPlayCounts, setCountryPlayCounts] = useState({});
  const [stationPlayCounts, setStationPlayCounts] = useState({});
  const [stationErrorCounts, setStationErrorCounts] = useState({});
  const [loadedStationCount, setLoadedStationCount] = useState(0);
  const [showAllStations, setShowAllStations] = useState(false);
  const [allStationsData, setAllStationsData] = useState([]);
  const [favoriteStations, setFavoriteStations] = useState(() => {
    try {
      const raw = localStorage.getItem('radio-favorites');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [recentStations, setRecentStations] = useState(() => {
    try {
      const raw = localStorage.getItem('radio-recents');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [collectionStations, setCollectionStations] = useState([]);
  const [collectionTitle, setCollectionTitle] = useState('');
  const [collectionSubtitle, setCollectionSubtitle] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Audio ref
  const audioRef = useRef(null);
  const showAllCountriesRef = useRef(showAllCountries);
  showAllCountriesRef.current = showAllCountries;

  // ===== UTILITY FUNCTIONS =====
  const showLoadingWithDetails = useCallback(() => setLoading(true), []);
  const showError = useCallback(() => setLoading(false), []);

  const isSameStation = useCallback((a, b) => {
    if (!a || !b) return false;
    if (a.id && b.id) return a.id === b.id;
    return a.name === b.name;
  }, []);

  const syncFavoritesToStorage = useCallback((next) => {
    try {
      localStorage.setItem('radio-favorites', JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  }, []);

  const syncRecentsToStorage = useCallback((next) => {
    try {
      localStorage.setItem('radio-recents', JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  }, []);

  const toggleFavoriteStation = useCallback((station) => {
    if (!station) return;
    setFavoriteStations((prev) => {
      const exists = prev.some((s) => isSameStation(s, station));
      const next = exists
        ? prev.filter((s) => !isSameStation(s, station))
        : [{ ...station }, ...prev].slice(0, 50);
      syncFavoritesToStorage(next);
      return next;
    });
  }, [isSameStation, syncFavoritesToStorage]);

  const pushRecentStation = useCallback((station) => {
    if (!station) return;
    setRecentStations((prev) => {
      const filtered = prev.filter((s) => !isSameStation(s, station));
      const next = [{ ...station }, ...filtered].slice(0, 25);
      syncRecentsToStorage(next);
      return next;
    });
  }, [isSameStation, syncRecentsToStorage]);

  const getCountryFlag = useCallback((countryCode) => {
    if (!countryCode) return '🏳️';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }, []);

  const formatPlayDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getAudioQuality = useCallback((station) => {
    if (station.bitrate) return `${station.bitrate}kbps`;
    return '128kbps';
  }, []);

  const getPopularityLevel = useCallback((station) => {
    return station.popularity_badge || '🆕 New';
  }, []);

  // ===== SEARCH — COUNTRIES =====
  const countrySearchTimeoutRef = useRef(null);

  const performCountrySearch = useCallback(async (term) => {
    if (term.length > 2) {
      try {
        const response = await apiService.searchCountries(term);
        const results = response.results || [];
        setFilteredCountries(results);
        logger.logUserAction('country_search', null, { searchTerm: term });
        analyticsService.trackSearch('country', term, results.length);
      } catch {
        const fallback = allCountriesData.filter((c) =>
          c.name.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredCountries(fallback);
      }
    } else {
      setFilteredCountries([]);
    }
  }, [allCountriesData]);

  const handleCountrySearchChange = useCallback((e) => {
    const term = e.target.value;
    setCountrySearchTerm(term);
    clearTimeout(countrySearchTimeoutRef.current);
    countrySearchTimeoutRef.current = setTimeout(() => performCountrySearch(term), 500);
  }, [performCountrySearch]);

  const handleCountrySearchKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      clearTimeout(countrySearchTimeoutRef.current);
      performCountrySearch(countrySearchTerm);
    }
  }, [performCountrySearch, countrySearchTerm]);

  const clearCountrySearch = useCallback(() => {
    setCountrySearchTerm('');
    setFilteredCountries([]);
  }, []);

  const toggleCountriesView = useCallback(() => {
    analyticsService.trackUserJourney('filter_toggle', { filter_type: 'countries', new_state: !showAllCountries }, 'countries');
    setCountrySearchTerm('');
    setFilteredCountries([]);

    if (showAllCountries) {
      const popular = allCountriesData
        .sort((a, b) => {
          const ap = countryPlayCounts[a.name] || 0, bp = countryPlayCounts[b.name] || 0;
          return ap !== bp ? bp - ap : a.name.localeCompare(b.name);
        })
        .slice(0, 30);
      setCountries(popular);
      setShowAllCountries(false);
    } else {
      if (allCountriesData.length > 0) {
        setCountries([...allCountriesData].sort((a, b) => a.name.localeCompare(b.name)));
        setShowAllCountries(true);
      }
    }
  }, [showAllCountries, allCountriesData, countryPlayCounts]);

  // ===== SEARCH — STATIONS =====
  const stationSearchTimeoutRef = useRef(null);

  const performStationSearch = useCallback(async (term) => {
    if (term.length > 2) {
      try {
        const response = await apiService.searchStations(term);
        const results = response.results || [];
        const filtered = selectedCountry
          ? results.filter((s) => s.country === selectedCountry || s.country_name === selectedCountry)
          : results;
        setFilteredStations(filtered);
        logger.logUserAction('station_search', null, { searchTerm: term, country: selectedCountry });
        analyticsService.trackSearch('station', term, filtered.length);
      } catch {
        const fallback = allStations.filter((s) =>
          s.name.toLowerCase().includes(term.toLowerCase()) ||
          (s.tags && s.tags.toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredStations(fallback);
      }
    } else {
      setFilteredStations([]);
    }
  }, [allStations, selectedCountry]);

  const handleStationSearchChange = useCallback((e) => {
    const term = e.target.value;
    setStationSearchTerm(term);
    clearTimeout(stationSearchTimeoutRef.current);
    stationSearchTimeoutRef.current = setTimeout(() => performStationSearch(term), 500);
  }, [performStationSearch]);

  const handleStationSearchKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      clearTimeout(stationSearchTimeoutRef.current);
      performStationSearch(stationSearchTerm);
    }
  }, [performStationSearch, stationSearchTerm]);

  const clearStationSearch = useCallback(() => {
    setStationSearchTerm('');
    setFilteredStations([]);
  }, []);

  const toggleStationsView = useCallback(() => {
    analyticsService.trackUserJourney('filter_toggle', { filter_type: 'stations', new_state: !showAllStations }, 'stations');

    const sorted = allStationsData
      .filter((s) => (s.url_resolved || s.effective_url) && s.name)
      .sort((a, b) => {
        const ap = stationPlayCounts[a.name] || 0, bp = stationPlayCounts[b.name] || 0;
        const ae = stationErrorCounts[a.name] || 0, be = stationErrorCounts[b.name] || 0;
        const at = ap + ae, bt = bp + be;
        const as = at > 0 ? ap / at : 0, bs = bt > 0 ? bp / bt : 0;
        if (as !== bs) return bs - as;
        if (ap !== bp) return bp - ap;
        return a.name.localeCompare(b.name);
      });

    if (showAllStations) {
      const pop = sorted.slice(0, 100);
      setAllStations(pop);
      setLoadedStationCount(pop.length);
      setShowAllStations(false);
    } else {
      setAllStations(sorted);
      setLoadedStationCount(sorted.length);
      setShowAllStations(true);
    }
  }, [showAllStations, allStationsData, stationPlayCounts, stationErrorCounts]);

  // ===== ANALYTICS HELPERS =====
  const trackSuccessfulPlay = useCallback(async (station) => {
    try {
      if (station.country_id) {
        await apiService.trackCountryPlay(station.country_id);
        setCountryPlayCounts((prev) => ({ ...prev, [station.country]: (prev[station.country] || 0) + 1 }));
      }
      if (station.id) {
        await apiService.trackStationPlay(station.id);
        setStationPlayCounts((prev) => ({ ...prev, [station.name]: (prev[station.name] || 0) + 1 }));
        setStationErrorCounts((prev) => ({ ...prev, [station.name]: 0 }));
      }
      analyticsService.trackAudioEngagement('play', station);
      analyticsService.trackUserJourney('station_select', {
        station_name: station.name,
        country_name: station.country_name || station.country,
      }, 'stations');
    } catch { /* silent */ }
  }, []);

  const trackPlayError = useCallback(async (station) => {
    try {
      if (station.id) {
        await apiService.trackStationError(station.id);
        setStationErrorCounts((prev) => ({ ...prev, [station.name]: (prev[station.name] || 0) + 1 }));
      }
      analyticsService.trackAudioEngagement('error', station, { error_type: 'playback_failed' });
    } catch { /* silent */ }
  }, []);

  const trackPlaySession = useCallback(async (station, startTime, endTime, duration, isCompleted = true) => {
    try {
      await apiService.trackStationPlaySession({
        session_id: analyticsService.getSessionId(),
        station_id: station.id,
        start_time: startTime,
        end_time: endTime,
        play_duration_seconds: duration,
        is_completed: isCompleted,
      });
    } catch (error) {
      logger.error('track_play_session_error', error);
    }
  }, []);

  const startPlaySession = useCallback((station) => {
    const session = {
      sessionId: analyticsService.getSessionId(),
      stationId: station.id,
      stationName: station.name,
      startTime: new Date().toISOString(),
      startTimestamp: Date.now(),
    };
    setCurrentPlaySession(session);
    return session;
  }, []);

  const endPlaySession = useCallback(async (reason = 'user_switched') => {
    if (!currentPlaySession || !currentPlayingStation) return;
    const duration = Math.floor(playDuration);
    await trackPlaySession(currentPlayingStation, currentPlaySession.startTime, new Date().toISOString(), duration, true);
    setCurrentPlaySession(null);
    logger.logUserAction('play_session_ended', null, { stationName: currentPlayingStation.name, duration, reason });
  }, [currentPlaySession, currentPlayingStation, playDuration, trackPlaySession]);

  // ===== AUDIO CONTROLS =====
  const handlePlayPause = useCallback(() => {
    if (!currentPlayingStation || !audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [currentPlayingStation]);

  // Find up to 5 stations sharing country or primary tag, excluding current
  const getSimilarStations = useCallback((station) => {
    const tag = (station.tags || '').split(',')[0].trim().toLowerCase();
    const country = (station.country || '').toLowerCase();
    const scored = allStations
      .filter((s) => s.name !== station.name && (s.url_resolved || s.effective_url || s.url))
      .map((s) => {
        const sTag = (s.tags || '').split(',')[0].trim().toLowerCase();
        const sCountry = (s.country || '').toLowerCase();
        const score = (tag && sTag === tag ? 2 : 0) + (country && sCountry === country ? 1 : 0);
        return { s, score };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score);
    return scored.slice(0, 5).map(({ s }) => s);
  }, [allStations]);

  const showStreamError = useCallback((type, station) => {
    const similar = getSimilarStations(station);
    setStreamError({ type, stationName: station?.name || 'Unknown station', similarStations: similar });
    setIsBuffering(false);
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
  }, [getSimilarStations]);

  const playStationDirect = useCallback(async (station) => {
    try {
      if (currentPlayingStation?.name === station.name) {
        handlePlayPause();
        return;
      }

      // Reset stream health state for fresh play
      setStreamError(null);
      setIsBuffering(false);
      autoRetryDoneRef.current = false;
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }

      if (currentPlaySession && currentPlayingStation) {
        await endPlaySession('user_switched');
      }

      showLoadingWithDetails();

      if (audioRef.current) audioRef.current.pause();

      setPlayDuration(0);
      setPlayStartTime(Date.now());
      setIsPlaying(true);
      setTotalPausedTime(0);
      setLastPauseTime(null);
      startPlaySession(station);

      setCurrentPlayingStation({
        ...station,
        trackingData: {
          stationName: station.name,
          country: station.country,
          country_name: station.country_name,
          tags: station.tags,
          lastAttempted: new Date().toISOString(),
          createdAt: new Date(),
        },
      });

      if (audioRef.current) {
        let audioUrl = station.url_resolved || station.effective_url || station.url;
        if (!audioUrl?.trim()) throw new Error('Invalid audio URL');
        const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
        if (isHttps && audioUrl.trim().toLowerCase().startsWith('http:')) {
          const base = apiService.getBaseURL().replace(/\/$/, '');
          audioUrl = `${base}/stream/${station.id}/`;
        }
        audioRef.current.src = audioUrl;
        audioRef.current.volume = currentVolume;
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) await playPromise;
      }

      setLoading(false);
      await trackSuccessfulPlay(station);
      pushRecentStation(station);
      logger.logUserAction('station_played', null, { stationName: station.name });
    } catch (error) {
      await trackPlayError(station);
      setLoading(false);
      const isPlayBlocked = error?.name === 'NotAllowedError' || (error?.message && /play|user gesture|interact/i.test(error.message));
      if (isPlayBlocked) {
        showStreamError('blocked', station);
      } else if (!navigator.onLine) {
        showStreamError('offline', station);
      } else {
        showStreamError('dead', station);
      }
      logger.error('play_station_error', error);
    }
  }, [
    currentVolume, showLoadingWithDetails,
    trackSuccessfulPlay, trackPlayError, pushRecentStation,
    currentPlaySession, currentPlayingStation, endPlaySession, startPlaySession, handlePlayPause,
    showStreamError,
  ]);

  const getNavigationList = useCallback(() => {
    if (currentPage === 'stations' && allStations.length) return allStations;
    if (currentPage === 'collection' && collectionStations.length) return collectionStations;
    if (recentStations.length) return recentStations;
    if (favoriteStations.length) return favoriteStations;
    return [];
  }, [currentPage, allStations, collectionStations, recentStations, favoriteStations]);

  const nextChannel = useCallback(() => {
    const list = getNavigationList();
    if (!list.length || !currentPlayingStation) return;
    const idx = list.findIndex((s) => s.name === currentPlayingStation.name);
    const nextIdx = idx === -1 ? 0 : (idx + 1) % list.length;
    playStationDirect(list[nextIdx]);
  }, [getNavigationList, currentPlayingStation, playStationDirect]);

  const previousChannel = useCallback(() => {
    const list = getNavigationList();
    if (!list.length || !currentPlayingStation) return;
    const idx = list.findIndex((s) => s.name === currentPlayingStation.name);
    const prevIdx = idx <= 0 ? list.length - 1 : idx - 1;
    playStationDirect(list[prevIdx]);
  }, [getNavigationList, currentPlayingStation, playStationDirect]);

  const handleVolumeChange = useCallback((vol) => {
    setCurrentVolume(vol);
    if (audioRef.current) audioRef.current.volume = vol;
  }, []);

  const handleIdentify = useCallback(async () => {
    if (!currentPlayingStation || identifyState === 'loading') return;
    // Close lyrics panel — only one panel open at a time
    setLyricsState(null);
    setLyricsTarget(null);
    const streamUrl = currentPlayingStation.url_resolved
      || currentPlayingStation.effective_url
      || currentPlayingStation.url;
    if (!streamUrl) return;
    setIdentifyState('loading');
    setArtistLinksState(null);
    try {
      const result = await apiService.identifySong(streamUrl);
      setIdentifyState(result);
      // Phase 2: fetch artist links in background if a song was found
      if (result && result.found && result.artist) {
        setArtistLinksState('loading');
        apiService.getArtistLinks(result.artist)
          .then((links) => setArtistLinksState(links || {}))
          .catch(() => setArtistLinksState({}));
      }
    } catch {
      setIdentifyState({ found: false });
    }
  }, [currentPlayingStation, identifyState]);

  const handleIdentifyRetry = useCallback(() => {
    setIdentifyState(null);
    setArtistLinksState(null);
    setTimeout(() => handleIdentify(), 50);
  }, [handleIdentify]);

  const handleOpenLyrics = useCallback(async ({ artist, title }) => {
    // Close identify panel — only one panel open at a time
    setIdentifyState(null);
    setArtistLinksState(null);
    setLyricsTarget({ artist, title });
    setLyricsState('loading');
    try {
      const url = `https://api.lyrics.ovh/v1/${encodeURIComponent(artist)}/${encodeURIComponent(title)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.lyrics) {
        setLyricsState({ lyrics: data.lyrics.trim() });
      } else {
        setLyricsState({ lyrics: null });
      }
    } catch {
      setLyricsState({ lyrics: null });
    }
  }, []);

  const preMuteVolumeRef = React.useRef(0.5);
  const handleMuteToggle = useCallback(() => {
    if (currentVolume === 0) {
      const restore = preMuteVolumeRef.current || 0.5;
      setCurrentVolume(restore);
      if (audioRef.current) audioRef.current.volume = restore;
    } else {
      preMuteVolumeRef.current = currentVolume;
      setCurrentVolume(0);
      if (audioRef.current) audioRef.current.volume = 0;
    }
  }, [currentVolume]);

  // ===== DATA LOADING =====
  const loadByCountry = useCallback(async (countryId, countryName) => {
    try {
      setLoading(true);

      const cacheHasFaviconData = allStationsData.some(
        (s) => s.favicon !== undefined || s.homepage !== undefined
      );
      if (allStationsData.length > 0 && selectedCountry === countryName && cacheHasFaviconData) {
        const cached = allStationsData
          .filter((s) => (s.url_resolved || s.effective_url) && s.name)
          .sort((a, b) => {
            const ap = stationPlayCounts[a.name] || 0, bp = stationPlayCounts[b.name] || 0;
            return bp - ap || a.name.localeCompare(b.name);
          });
        setAllStations(cached);
        setLoadedStationCount(cached.length);
        setShowAllStations(true);
        setLoading(false);
        return;
      }

      const response = await apiService.getStations(countryName, '', 1, 2000);
      const stations = response.results || [];

      if (!stations.length) {
        setAllStations([]);
        setLoadedStationCount(0);
        setLoading(false);
        return;
      }

      setAllStationsData(stations);

      const sorted = stations
        .filter((s) => (s.url_resolved || s.effective_url) && s.name)
        .sort((a, b) => {
          const ap = a.play_count || 0, bp = b.play_count || 0;
          const ae = a.error_count || 0, be = b.error_count || 0;
          const at = ap + ae, bt = bp + be;
          const as = at > 0 ? ap / at : 0, bs = bt > 0 ? bp / bt : 0;
          if (as !== bs) return bs - as;
          if (ap !== bp) return bp - ap;
          return a.name.localeCompare(b.name);
        });

      const playCounts = {}, errorCounts = {};
      sorted.forEach((s) => {
        playCounts[s.name] = s.play_count || 0;
        errorCounts[s.name] = s.error_count || 0;
      });
      setStationPlayCounts(playCounts);
      setStationErrorCounts(errorCounts);

      setAllStations(sorted);
      setLoadedStationCount(sorted.length);
      setShowAllStations(true);
      setLoading(false);
    } catch (error) {
      showError();
      logger.error('load_country_stations_error', error);
      setModalMessage(error?.message || 'Unable to load stations. Please try again.');
      setShowErrorModal(true);
      setLoading(false);
    }
  }, [allStationsData, selectedCountry, stationPlayCounts, showError]);

  const handleCountrySelect = useCallback((country) => {
    setSelectedCountry(country.name);
    setCurrentPage('stations');
    showLoadingWithDetails();
    loadByCountry(country.id, country.name);
    analyticsService.trackUserJourney('country_select', { country_name: country.name, country_id: country.id }, 'countries');
  }, [loadByCountry, showLoadingWithDetails]);

  const handleBackToCountries = useCallback(() => {
    setCurrentPage('home');
    setSelectedCountry('');
    setAllStations([]);
  }, []);

  // Navigate from sidebar
  const handleNavigate = useCallback((page) => {
    if (page === 'home') {
      setSelectedCountry('');
      setAllStations([]);
    }
    setCurrentPage(page);
  }, []);

  const handleOpenCollection = useCallback((title, subtitle, stations) => {
    if (!stations || stations.length === 0) return;
    setCollectionStations(stations);
    setCollectionTitle(title || 'Stations');
    setCollectionSubtitle(subtitle || '');
    setCurrentPage('collection');
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // ===== CONTACT FORM =====
  const handleContactInputChange = useCallback((field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleContactSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.topic || !contactForm.subject || !contactForm.message) {
      setModalMessage('Please fill in all required fields');
      setShowErrorModal(true);
      return;
    }
    setContactLoading(true);
    try {
      await apiService.submitContactForm({ ...contactForm, user_agent: navigator.userAgent });
      setContactForm({ name: '', email: '', topic: '', subject: '', message: '' });
      setModalMessage('Thank you for your message! We will get back to you soon.');
      setShowSuccessModal(true);
      setShowContactModal(false);
    } catch (error) {
      setModalMessage(error?.message || 'Sorry, there was an error sending your message. Please try again.');
      setShowErrorModal(true);
    } finally {
      setContactLoading(false);
    }
  }, [contactForm]);

  // ===== COUNTRY LOADING =====
  const loadCountries = useCallback(async () => {
    try {
      setLoading(true);
      let allFetched = [], page = 1, hasMore = true;
      while (hasMore) {
        const response = await apiService.getCountries('', page, 50);
        const data = response.results || [];
        allFetched = [...allFetched, ...data];
        hasMore = data.length === 50 && response.next;
        page++;
      }

      setAllCountriesData(allFetched);

      const playCounts = {};
      allFetched.forEach((c) => { playCounts[c.name] = c.play_count || 0; });
      setCountryPlayCounts(playCounts);

      setCountries([...allFetched].sort((a, b) => a.name.localeCompare(b.name)));

      setLoading(false);
    } catch (error) {
      logger.error('load_countries_error', error);
      setModalMessage(error?.message || 'Unable to load content. Please try again.');
      setShowErrorModal(true);
      setLoading(false);
    }
  }, []);

  const checkSyncStatus = useCallback(async () => {
    try {
      const status = await apiService.getSyncStatus();
      setSyncStatus(status);
      if (status.sync_status === 'completed' && countries.length === 0) {
        loadCountries();
      }
    } catch { /* silent */ }
  }, [countries.length, loadCountries]);

  // ===== EFFECTS =====
  useEffect(() => {
    analyticsService.trackVisitor();
    analyticsService.trackVisitorSession();
    analyticsService.trackUserJourney('page_view', {}, 'countries');
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(countrySearchTimeoutRef.current);
      clearTimeout(stationSearchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (currentPlaySession && currentPlayingStation) {
        const duration = Math.floor(playDuration);
        if (duration > 0) {
          trackPlaySession(currentPlayingStation, currentPlaySession.startTime, new Date().toISOString(), duration, true);
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCountries();
    checkSyncStatus();
  }, [loadCountries, checkSyncStatus]);

  useEffect(() => {
    let interval = null;
    if (countries.length === 0) {
      interval = setInterval(checkSyncStatus, 10000);
    } else if (syncStatus?.sync_status === 'running') {
      interval = setInterval(checkSyncStatus, 10000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [countries.length, syncStatus?.sync_status, checkSyncStatus]);

  useEffect(() => {
    if (syncStatus?.sync_status === 'completed' && countries.length === 0) {
      loadCountries();
    }
  }, [syncStatus?.sync_status, countries.length, loadCountries]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = currentVolume;
      audioRef.current.preload = 'metadata';
    }
  }, [currentVolume]);

  // Play duration timer
  useEffect(() => {
    let interval = null;
    if (playStartTime && currentPlayingStation && isPlaying) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - playStartTime) / 1000) - totalPausedTime;
        setPlayDuration(Math.max(0, elapsed));
      }, 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [playStartTime, currentPlayingStation, isPlaying, totalPausedTime]);

  // Audio event listeners
  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    const handlePlay = () => {
      if (lastPauseTime) {
        const pauseDuration = (Date.now() - lastPauseTime) / 1000;
        setTotalPausedTime((prev) => prev + pauseDuration);
        setLastPauseTime(null);
      }
      setIsPlaying(true);
      if (currentPlayingStation) analyticsService.trackAudioEngagement('play', currentPlayingStation);
    };

    const handlePause = () => {
      if (playStartTime && currentPlayingStation) {
        setLastPauseTime(Date.now());
        analyticsService.trackAudioEngagement('pause', currentPlayingStation, {
          duration_played: (Date.now() - playStartTime) / 1000,
        });
      }
      setIsPlaying(false);
    };

    const handleEnded = () => {
      if (playStartTime && currentPlayingStation) {
        analyticsService.trackAudioEngagement('ended', currentPlayingStation, {
          duration_played: (Date.now() - playStartTime) / 1000,
        });
      }
      setIsPlaying(false);
    };

    // Stream drops mid-play — auto-retry once, then show error
    const handleError = () => {
      if (currentPlayingStation) {
        analyticsService.trackAudioEngagement('error', currentPlayingStation, { error_type: 'audio_error' });
      }
      setIsPlaying(false);
      setIsBuffering(false);
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }

      if (currentPlayingStation) {
        if (!autoRetryDoneRef.current) {
          // Silent auto-retry once after 2 s
          autoRetryDoneRef.current = true;
          setTimeout(() => {
            if (audioRef.current && audioRef.current.src) {
              audioRef.current.load();
              audioRef.current.play().catch(() => {
                showStreamError('dropped', currentPlayingStation);
              });
            }
          }, 2000);
        } else {
          showStreamError('dropped', currentPlayingStation);
        }
      }
    };

    // Buffering started
    const handleWaiting = () => {
      setIsBuffering(true);
      // If still buffering after 15 s, show stall dialog
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      stallTimerRef.current = setTimeout(() => {
        if (currentPlayingStation) showStreamError('stalled', currentPlayingStation);
      }, 15000);
    };

    // Buffering resolved
    const handleCanPlay = () => {
      setIsBuffering(false);
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    };

    const handleVolumeChange = () => {
      if (currentPlayingStation) {
        analyticsService.trackAudioEngagement('volume_change', currentPlayingStation, { volume_level: audio.volume });
      }
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('playing', handleCanPlay);
    audio.addEventListener('volumechange', handleVolumeChange);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('playing', handleCanPlay);
      audio.removeEventListener('volumechange', handleVolumeChange);
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    };
  }, [currentPlayingStation, playStartTime, lastPauseTime, showStreamError]);

  // Stream error retry
  const handleStreamErrorRetry = useCallback(() => {
    if (!currentPlayingStation) { setStreamError(null); return; }
    setStreamError(null);
    autoRetryDoneRef.current = false;
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.play().catch(() => showStreamError('dead', currentPlayingStation));
    }
  }, [currentPlayingStation, showStreamError]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const active = document.activeElement;
      const isTyping = active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.contentEditable === 'true'
      );
      if (isTyping) return;

      if (currentPage === 'stations' && selectedCountry && allStations.length > 0) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            previousChannel();
            break;
          case 'ArrowRight':
            e.preventDefault();
            nextChannel();
            break;
          case ' ':
            e.preventDefault();
            if (currentPlayingStation) {
              if (audioRef.current?.paused) audioRef.current.play();
              else audioRef.current?.pause();
            }
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, selectedCountry, allStations.length, currentPlayingStation, previousChannel, nextChannel]);

  const isCurrentFavorite = currentPlayingStation
    ? favoriteStations.some((s) => isSameStation(s, currentPlayingStation))
    : false;

  // ===== RENDER =====
  return (
    <div className="app-layout">
      {/* Persistent player bar (top) */}
      <NowPlayingBar
        currentPlayingStation={currentPlayingStation}
        isPlaying={isPlaying}
        currentVolume={currentVolume}
        playDuration={playDuration}
        formatPlayDuration={formatPlayDuration}
        getAudioQuality={getAudioQuality}
        onPlayPause={handlePlayPause}
        onVolumeChange={handleVolumeChange}
        onMuteToggle={handleMuteToggle}
        onIdentify={handleIdentify}
        identifyState={identifyState}
        onPrevious={previousChannel}
        onNext={nextChannel}
        isCurrentFavorite={isCurrentFavorite}
        onToggleFavorite={() => currentPlayingStation && toggleFavoriteStation(currentPlayingStation)}
        audioRef={audioRef}
        isBuffering={isBuffering}
      />

      {/* Stream error dialog */}
      <StreamErrorModal
        error={streamError}
        onDismiss={() => setStreamError(null)}
        onRetry={handleStreamErrorRetry}
        onPlayStation={playStationDirect}
      />

      {/* Song identification panel — right drawer on desktop, bottom sheet on mobile */}
      <IdentifyPanel
        identifyState={identifyState}
        artistLinksState={artistLinksState}
        currentStation={currentPlayingStation}
        onDismiss={() => { setIdentifyState(null); setArtistLinksState(null); }}
        onRetry={handleIdentifyRetry}
        onOpenLyrics={handleOpenLyrics}
      />

      <LyricsPanel
        lyricsState={lyricsState}
        artist={lyricsTarget?.artist}
        title={lyricsTarget?.title}
        onDismiss={() => { setLyricsState(null); setLyricsTarget(null); }}
      />

      <button
        className="sidebar-toggle-btn"
        type="button"
        onClick={toggleSidebar}
        aria-label="Open menu"
      >
        ☰
      </button>

      {/* Sidebar (desktop) + Bottom nav (mobile) */}
      <AppSidebar
        currentPage={currentPage}
        selectedCountry={selectedCountry}
        onNavigate={handleNavigate}
        theme={theme}
        onToggleTheme={toggleTheme}
        onShowAbout={() => setShowAboutModal(true)}
        onShowContact={() => setShowContactModal(true)}
        onShowPrivacy={() => setShowPrivacyModal(true)}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Scrollable main content */}
      <main className="main-content">
        {currentPage === 'home' && (
          <HomePage
            countries={countries}
            currentPlayingStation={currentPlayingStation}
            isPlaying={isPlaying}
            audioRef={audioRef}
            getCountryFlag={getCountryFlag}
            favoriteStations={favoriteStations}
            recentStations={recentStations}
            onStationSelect={playStationDirect}
            onToggleFavorite={toggleFavoriteStation}
            onOpenCollection={handleOpenCollection}
            onCountrySelect={handleCountrySelect}
            onBrowseAllCountries={() => setCurrentPage('countries')}
          />
        )}
        {currentPage === 'countries' && (
          <CountriesPage
            countries={countries}
            filteredCountries={filteredCountries}
            countrySearchTerm={countrySearchTerm}
            loading={loading}
            syncStatus={syncStatus}
            countryPlayCounts={countryPlayCounts}
            getCountryFlag={getCountryFlag}
            onCountrySelect={handleCountrySelect}
            onSearchChange={handleCountrySearchChange}
            onSearchKeyPress={handleCountrySearchKeyPress}
            onClearSearch={clearCountrySearch}
            onRefresh={loadCountries}
          />
        )}
        {currentPage === 'stations' && (
          <StationsPage
            stations={allStations}
            filteredStations={filteredStations}
            stationSearchTerm={stationSearchTerm}
            selectedCountry={selectedCountry}
            loading={loading}
            loadedStationCount={loadedStationCount}
            currentPlayingStation={currentPlayingStation}
            isPlaying={isPlaying}
            audioRef={audioRef}
            getPopularityLevel={getPopularityLevel}
            favoriteStations={favoriteStations}
            onToggleFavorite={toggleFavoriteStation}
            onStationSelect={playStationDirect}
            onBack={handleBackToCountries}
            onSearchChange={handleStationSearchChange}
            onSearchKeyPress={handleStationSearchKeyPress}
            onClearSearch={clearStationSearch}
          />
        )}
        {currentPage === 'collection' && (
          <StationsPage
            stations={collectionStations}
            filteredStations={filteredStations}
            stationSearchTerm={stationSearchTerm}
            selectedCountry=""
            loading={loading}
            loadedStationCount={collectionStations.length}
            currentPlayingStation={currentPlayingStation}
            isPlaying={isPlaying}
            audioRef={audioRef}
            getPopularityLevel={getPopularityLevel}
            favoriteStations={favoriteStations}
            onToggleFavorite={toggleFavoriteStation}
            onStationSelect={playStationDirect}
            onBack={() => setCurrentPage('home')}
            onSearchChange={handleStationSearchChange}
            onSearchKeyPress={handleStationSearchKeyPress}
            onClearSearch={clearStationSearch}
            pageTitle={collectionTitle}
            pageSubtitle={collectionSubtitle}
          />
        )}

        {/* Footer */}
        <footer className="app-footer">
          <div className="footer-links">
            <button className="footer-link" onClick={() => setShowAboutModal(true)}>About Us</button>
            <span className="footer-sep">·</span>
            <button className="footer-link" onClick={() => setShowContactModal(true)}>Contact</button>
            <span className="footer-sep">·</span>
            <button className="footer-link" onClick={() => setShowPrivacyModal(true)}>Privacy Policy</button>
          </div>
          <div className="footer-copyright">© 2025 RadioStreamIn.com. All rights reserved.</div>
          <div className="footer-tagline">Live radio stations from around the world</div>
        </footer>
      </main>

      {/* ── MODALS ── */}

      {/* About */}
      {showAboutModal && (
        <div className="modal-overlay" onClick={() => setShowAboutModal(false)}>
          <div className="modal-content about-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <span className="modal-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                </span>
                About RadioStreamIn.com
              </div>
              <button className="modal-close" onClick={() => setShowAboutModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              <div className="static-content">
                <div className="about-hero">
                  <div className="about-logo-mark">RS</div>
                  <p className="about-tagline">Free internet radio from every corner of the world.</p>
                </div>

                <section className="about-section">
                  <h3>What is RadioStreamIn.com?</h3>
                  <p>RadioStreamIn.com is a free, ad-light internet radio aggregator. We bring together thousands of publicly available radio stations from across the globe into a single, easy-to-use player — so you can discover and listen without jumping between dozens of individual station websites.</p>
                </section>

                <section className="about-section">
                  <h3>How it works</h3>
                  <p>We do not own, operate, broadcast, or host any radio station or audio content. All audio streams are delivered directly from each station's own public server to your device. We simply provide the interface to discover and tune in.</p>
                  <p>Station metadata (names, genres, countries, stream URLs) is sourced from the open-source <strong>Radio Browser</strong> community database (<a href="https://www.radio-browser.info" target="_blank" rel="noopener noreferrer">radio-browser.info</a>), which is maintained collaboratively by volunteers worldwide.</p>
                </section>

                <section className="about-section">
                  <h3>Intellectual property</h3>
                  <p>All station names, logos, trademarks, call signs, and branding belong exclusively to their respective owners and broadcasters. RadioStreamIn.com makes no claim of ownership over any station name, logo, or content. Station logos displayed are retrieved via publicly accessible favicon services.</p>
                </section>

                <section className="about-section">
                  <h3>Station owners</h3>
                  <p>If you are a station owner or rights holder and wish to have your station's information corrected or removed from our platform, please <button className="inline-link-btn" onClick={() => { setShowAboutModal(false); setShowContactModal(true); }}>contact us</button> and we will act promptly.</p>
                </section>

                <div className="about-footer-note">
                  <p>RadioStreamIn.com &copy; {new Date().getFullYear()} &mdash; All third-party trademarks are the property of their respective owners.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content contact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <span className="modal-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                </span>
                Contact Us
              </div>
              <button className="modal-close" onClick={() => setShowContactModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              <p className="contact-intro">
                Have a question, spotted an issue, or want to suggest a feature? Fill in the form below and we'll get back to you as soon as possible.
              </p>
              <form onSubmit={handleContactSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="contact-name">NAME *</label>
                  <input id="contact-name" type="text" value={contactForm.name}
                    onChange={(e) => handleContactInputChange('name', e.target.value)}
                    placeholder="Your name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-email">EMAIL *</label>
                  <input id="contact-email" type="email" value={contactForm.email}
                    onChange={(e) => handleContactInputChange('email', e.target.value)}
                    placeholder="your@email.com" required />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-topic">TOPIC *</label>
                  <select id="contact-topic" value={contactForm.topic}
                    onChange={(e) => handleContactInputChange('topic', e.target.value)} required>
                    <option value="">Select a topic…</option>
                    <option value="feedback">General Feedback</option>
                    <option value="bug-report">Bug Report</option>
                    <option value="feature-request">Feature Request</option>
                    <option value="technical-issue">Technical Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="contact-subject">SUBJECT *</label>
                  <input id="contact-subject" type="text" value={contactForm.subject}
                    onChange={(e) => handleContactInputChange('subject', e.target.value)}
                    placeholder="Brief description" required />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-message">MESSAGE *</label>
                  <textarea id="contact-message" value={contactForm.message}
                    onChange={(e) => handleContactInputChange('message', e.target.value)}
                    placeholder="Your message…" rows="5" required />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowContactModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-send" disabled={contactLoading}>
                    {contactLoading ? 'Sending…' : 'Send message'}
                  </button>
                </div>
                <div className="direct-contact">
                  Prefer direct contact? Email us at{' '}
                  <a href="mailto:ravinain100@gmail.com">ravinain100@gmail.com</a>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Privacy */}
      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="modal-content privacy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <span className="modal-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </span>
                Privacy Policy
              </div>
              <button className="modal-close" onClick={() => setShowPrivacyModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              <div className="static-content">
                <p className="policy-effective">Effective date: January 1, 2025</p>

                <section className="about-section">
                  <h3>1. Who we are</h3>
                  <p>RadioStreamIn.com is a free internet radio aggregator. We do not produce, host, or control any radio stream or audio content. All streams play directly from each station's own servers.</p>
                </section>

                <section className="about-section">
                  <h3>2. Information we collect</h3>
                  <p>We collect minimal data to operate the service:</p>
                  <ul className="policy-list">
                    <li><strong>Usage analytics</strong> — anonymised data such as which stations are played, listening duration, and general geographic region (country level). No personally identifiable information is attached.</li>
                    <li><strong>Favourites &amp; preferences</strong> — stored locally in your browser (localStorage). We never send this data to our servers.</li>
                    <li><strong>Contact form submissions</strong> — if you reach out via our contact form, we collect your name, email, and message solely to respond to your enquiry.</li>
                  </ul>
                </section>

                <section className="about-section">
                  <h3>3. Third-party services</h3>
                  <p>Our platform interacts with the following third-party services:</p>
                  <ul className="policy-list">
                    <li><strong>Radio Browser API</strong> (radio-browser.info) — provides station metadata. Subject to their own terms.</li>
                    <li><strong>Google Favicon Service</strong> — used to fetch station logo images from public URLs. Your IP may be exposed to Google when loading these images.</li>
                    <li><strong>country.is</strong> — used to detect your approximate country to suggest local stations. Only your IP address is used and is not stored by us.</li>
                  </ul>
                  <p>We do not sell or share your data with any other third party for advertising or profiling purposes.</p>
                </section>

                <section className="about-section">
                  <h3>4. Cookies</h3>
                  <p>We use only functional cookies/localStorage to remember your preferences (theme, volume, favourites). We do not use tracking or advertising cookies.</p>
                </section>

                <section className="about-section">
                  <h3>5. Audio streams &amp; third-party content</h3>
                  <p>When you play a station, your device connects directly to that station's streaming server. RadioStreamIn.com has no control over the content broadcast by any station. The station's own privacy policy and terms apply to that connection.</p>
                </section>

                <section className="about-section">
                  <h3>6. Data retention</h3>
                  <p>Contact form data is retained only as long as necessary to resolve your enquiry and is then deleted. Anonymised analytics data may be retained indefinitely in aggregate form.</p>
                </section>

                <section className="about-section">
                  <h3>7. Your rights</h3>
                  <p>You may request deletion of any contact form data we hold about you by emailing us. Since analytics are fully anonymised, individual deletion is not applicable.</p>
                </section>

                <section className="about-section">
                  <h3>8. Changes to this policy</h3>
                  <p>We may update this Privacy Policy from time to time. Continued use of the service after changes constitutes acceptance of the updated policy.</p>
                </section>

                <section className="about-section">
                  <h3>9. Contact</h3>
                  <p>Questions about this policy? <button className="inline-link-btn" onClick={() => { setShowPrivacyModal(false); setShowContactModal(true); }}>Contact us</button> and we'll be happy to help.</p>
                </section>

                <div className="about-footer-note">
                  <p>RadioStreamIn.com &copy; {new Date().getFullYear()} &mdash; We do not own or operate any radio station.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><span className="modal-icon">✅</span> Success</div>
              <button className="modal-close" onClick={() => setShowSuccessModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 'var(--space-5)', color: 'var(--text-secondary)' }}>{modalMessage}</p>
              <button className="btn-send" onClick={() => setShowSuccessModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="modal-content error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <span className="modal-icon">{modalMessage === RATE_LIMIT_MESSAGE ? '⏳' : '❌'}</span>
                {modalMessage === RATE_LIMIT_MESSAGE ? 'Please slow down' : 'Error'}
              </div>
              <button className="modal-close" onClick={() => setShowErrorModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 'var(--space-5)', color: 'var(--text-secondary)' }}>{modalMessage}</p>
              <button className="btn-cancel" onClick={() => setShowErrorModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      {showInfoModal && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><span className="modal-icon">ℹ️</span> Information</div>
              <button className="modal-close" onClick={() => setShowInfoModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 'var(--space-5)', color: 'var(--text-secondary)' }}>{modalMessage}</p>
              <button className="btn-send" onClick={() => setShowInfoModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        id="radioPlayer"
        style={{ display: 'none' }}
        crossOrigin="anonymous"
        preload="metadata"
        onError={(e) => {
          logger.error('Audio playback error', { error: e.target.error, src: e.target.src, station: currentPlayingStation?.name });
          setTimeout(() => {
            if (audioRef.current?.paused) {
              if (currentPlayingStation) trackPlayError(currentPlayingStation);
              setModalMessage('Station not available at the moment. Please try again.');
              setShowErrorModal(true);
            }
          }, 3000);
        }}
        onAbort={(e) => {
          logger.error('Audio playback aborted', { src: e.target.src, station: currentPlayingStation?.name });
          if (currentPlayingStation) trackPlayError(currentPlayingStation);
          setModalMessage('Station not available at the moment. Please try again.');
          setShowErrorModal(true);
        }}
        onCanPlay={() => { setShowErrorModal(false); setModalMessage(''); }}
        onLoadStart={() => { setShowErrorModal(false); setModalMessage(''); }}
        onPlaying={() => { setShowErrorModal(false); setModalMessage(''); }}
      >
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default Radio;
